import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { useWizardStore } from "@/store/webinar/wizardStore";
import { AvailabilityIndicator } from "@/components/webinar/inscricao/AvailabilityIndicator";
import { TrustSeals } from "@/components/webinar/inscricao/TrustSeals";
import { FormFieldDynamic } from "@/components/webinar/inscricao/FormFieldDynamic";
import { Check, Calendar, MapPin } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export default function WebinarInscricao() {
  useLanguage();
  const { data } = useWizardStore();
  const config = data.login_config;
  const { toast } = useToast();

  const [formData, setFormData] = useState<Record<string, string>>({});
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validateWhatsApp = (phone: string) => {
    const numbers = phone.replace(/\D/g, '');
    return numbers.length >= 10;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors: Record<string, string> = {};
    
    // Validar campos
    config.form_fields.forEach((field: any) => {
      if (!field.enabled) return;
      
      if (field.required && !formData[field.name]) {
        newErrors[field.name] = 'Campo obrigatório';
      }
      
      if (field.type === 'email' && formData[field.name] && !validateEmail(formData[field.name])) {
        newErrors[field.name] = 'E-mail inválido';
      }
      
      if (field.name === 'whatsapp' && formData[field.name] && !validateWhatsApp(formData[field.name])) {
        newErrors[field.name] = 'WhatsApp inválido';
      }
    });
    
    if (config.lgpd_enabled && config.lgpd_required && !consent) {
      newErrors.consent = translateInterfaceText("Você deve aceitar os termos");
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
      toast({
        title: translateInterfaceText("Erro no formulário"),
        description: translateInterfaceText("Por favor, corrija os erros antes de continuar."),
        variant: "destructive"
      });
      return;
    }
    
    setIsSubmitting(true);
    
    // Simular envio
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast({
      title: translateInterfaceText("Inscrição realizada!"),
      description: translateInterfaceText("Você será redirecionado para a sala...")
    });
    
    // Redirecionar baseado no comportamento
    if (config.cta_behavior === 'submit_thankyou' && config.cta_thankyou_url) {
      window.location.href = config.cta_thankyou_url;
    } else {
      // Redirecionar para sala (implementar rota)
      console.log('Redirect to webinar room');
    }
  };

  const benefits = [
    translateInterfaceText("Configuração guiada passo a passo"),
    translateInterfaceText("Sem plugins complexos"),
    translateInterfaceText("Entrega de replay automática")
  ];

  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b">
        <div className="container mx-auto px-4 py-4">
          <div className={`flex ${config.logo_alignment === 'center' ? 'justify-center' : config.logo_alignment === 'right' ? 'justify-end' : 'justify-start'}`}>
            {config.logo_url ? (
              <img src={config.logo_url} alt={translateInterfaceText("Logo")} className="h-12 object-contain" />
            ) : (
              <div className="text-xl font-bold text-primary">{translateInterfaceText("Host VSL")}</div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-2 gap-12 items-start max-w-6xl mx-auto">
          {/* Left Column - Text */}
          <div className="space-y-6 animate-fade-in">
            {config.event_badge_enabled && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/10 text-green-700 dark:text-green-400 text-sm font-medium">
                <Calendar className="h-4 w-4" />
                {translateInterfaceText("Sessão atualizada hoje")}</div>
            )}
            
            <h1 className="text-4xl md:text-5xl font-bold leading-tight">
              {config.page_title}
            </h1>
            
            <p className="text-lg text-muted-foreground">
              {config.page_subtitle}
            </p>
            
            <ul className="space-y-3">
              {benefits.map((benefit, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="rounded-full bg-green-500/10 p-1 mt-0.5">
                    <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                  </div>
                  <span className="text-foreground">{benefit}</span>
                </li>
              ))}
            </ul>

            {config.countdown_enabled && (
              <div className="p-4 rounded-lg border bg-card">
                <p className="text-sm text-muted-foreground mb-2">{translateInterfaceText("Evento começa em:")}</p>
                <div className="text-3xl font-bold text-primary">00:45:30</div>
              </div>
            )}

            {config.timezone_indicator && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4" />
                <span>{translateInterfaceText("Horário detectado:")}{" "}{userTimezone}</span>
              </div>
            )}
          </div>

          {/* Right Column - Enrollment Card */}
          <div className="lg:sticky lg:top-6">
            <Card 
              className="shadow-2xl animate-fade-in"
              style={{
                backgroundColor: config.background_color,
                backgroundImage: config.background_image_url ? `url(${config.background_image_url})` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backdropFilter: config.background_blur ? 'blur(8px)' : undefined
              }}
            >
              <CardContent className="p-6 space-y-6">
                <AvailabilityIndicator
                  type={config.availability_type}
                  thickness={config.availability_thickness}
                  colorBase={config.availability_color_base}
                  colorProgress={config.availability_color_progress}
                  progress={config.availability_initial}
                  label={config.availability_label}
                />

                <form onSubmit={handleSubmit} className="space-y-4">
                  {config.form_fields
                    .filter((field: any) => field.enabled)
                    .map((field: any) => (
                      <FormFieldDynamic
                        key={field.id}
                        field={field}
                        value={formData[field.name] || ''}
                        onChange={(value) => setFormData({ ...formData, [field.name]: value })}
                        error={errors[field.name]}
                      />
                    ))}

                  {config.lgpd_enabled && (
                    <div className="flex items-start gap-2">
                      <Checkbox
                        id="consent"
                        checked={consent}
                        onCheckedChange={(checked) => setConsent(checked as boolean)}
                      />
                      <label htmlFor="consent" className="text-xs text-muted-foreground leading-tight cursor-pointer">
                        {config.lgpd_text}
                        {config.lgpd_required && <span className="text-destructive ml-1">*</span>}
                      </label>
                    </div>
                  )}
                  {errors.consent && (
                    <p className="text-xs text-destructive">{errors.consent}</p>
                  )}

                  <Button
                    type="submit"
                    className="w-full h-12 text-base font-semibold"
                    variant={config.cta_style === 'outline' ? 'outline' : 'default'}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? translateInterfaceText("Enviando...") : config.cta_label}
                  </Button>
                </form>

                <TrustSeals
                  seals={config.trust_seals}
                  enabled={config.trust_seals_enabled}
                />

                {config.testimonial_enabled && config.testimonial && (
                  <div className="p-4 rounded-lg bg-muted/50 space-y-2">
                    <div className="flex items-center gap-3">
                      {config.testimonial.avatar && (
                        <img
                          src={config.testimonial.avatar}
                          alt={config.testimonial.name}
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      )}
                      <div>
                        <p className="font-medium text-sm">{config.testimonial.name}</p>
                        <p className="text-xs text-muted-foreground">{config.testimonial.role}</p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground italic">"{config.testimonial.text}"</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t mt-12">
        <div className="container mx-auto px-4 py-6">
          <div className="flex justify-center gap-6 flex-wrap text-sm text-muted-foreground">
            {config.footer_links.map((link: any) => (
              <a
                key={link.id}
                href={link.url}
                className="hover:text-foreground transition-colors"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
